import { upperFirst } from "lodash-es"

import { ReviewPatternTypeKey } from "@/constants/terminal/review-pattern-type-key"
import { SparkTypeKey } from "@/constants/terminal/spark-type-key"

// The Detection Label: the short buyer word each Terminal Policy 1.0
// Detection type wears on its pill, filter chip, Report and board row
// (COMPANY terminal/_scratch/detection-label-urgent-release-2026-10-27.md,
// from the Detection Taxonomy, Dhanur, 27 Oct 2026). Types may share a Label;
// sharing changes neither their identity, their Class nor how they score. The
// list's order is the buyer's reading order inside a lane: Fire, then Smoke,
// then Spark types. A known type always wears its Label here, whatever word
// its File stored, so existing Files read in the current vocabulary.
const detectionTypeLabels: [typeKey: string, label: string][] = [
  ["license_operating_restriction", "LICENSE ACTION"],
  ["license_disciplinary_order", "LICENSE ACTION"],
  ["qualifier_license_restriction", "QUALIFIER ACTION"],
  ["qualifier_license_order", "QUALIFIER ACTION"],
  ["license_bond_restriction", "BOND SUSPENSION"],
  ["license_admonishment", "LICENSE WARNING"],
  ["license_administrative_suspension", "COVERAGE LAPSE"],
  ["creditor_default_cluster", "COLLECTIONS"],
  ["bankruptcy_proceeding", "BANKRUPTCY"],
  ["government_enforcement_action", "AGENCY ACTION"],
  ["government_enforcement_order", "AGENCY ORDER"],
  ["civil_judgment", "LAWSUIT LOST"],
  ["tax_lien", "TAX LIEN"],
  ["judgment_lien", "JUDGMENT LIEN"],
  ["workplace_safety_citation", "SAFETY VIOLATION"],
  ["operational_cessation", "OUT OF BUSINESS"],
  ["individual_negative_association", "NEGATIVE ASSOCIATION"],

  ["customer_reported_deception_pattern", "DECEPTION"],
  [ReviewPatternTypeKey.SALES_CONDUCT, "SALES MISCONDUCT"],
  [ReviewPatternTypeKey.WORKMANSHIP, "FAULTY WORK"],
  [ReviewPatternTypeKey.POST_SALE_SUPPORT, "UNDEPENDABLE"],
  [ReviewPatternTypeKey.PROJECT_MANAGEMENT, "BADLY RUN JOBS"],
  [ReviewPatternTypeKey.VALUE, "POOR VALUE"],
  [ReviewPatternTypeKey.LONG_TERM_SATISFACTION, "UNDEPENDABLE"],
  ["activity_collapse", "ACTIVITY DECLINE"],
  ["customer_complaint_pattern", "REPUTATION RISK"],
  ["review_manipulation", "SUSPECT REVIEWS"],
  ["customer_harm_pattern", "ABANDONMENT"],
  ["stacked_secured_claims", "DEBT STACKING"],
  ["pending_civil_proceeding", "LAWSUIT PENDING"],
  ["pending_consumer_lawsuits", "LAWSUIT PENDING"],
  ["entity_recycling", "ENTITY RECYCLING"],
  ["entity_standing_impairment", "ENTITY LAPSED"],
  ["workplace_injury_pattern", "INJURY PATTERN"],
  ["license_qualification_lapse", "CREDENTIAL RISK"],
  ["license_pending_discipline", "LICENSE AT RISK"],
  ["license_pending_complaint", "BOARD COMPLAINT"],

  [SparkTypeKey.DECEPTION, "DECEPTION"],
  [SparkTypeKey.SALES_CONDUCT, "SALES MISCONDUCT"],
  [SparkTypeKey.WORKMANSHIP, "FAULTY WORK"],
  [SparkTypeKey.POST_SALE_SUPPORT, "POOR AFTERCARE"],
  [SparkTypeKey.PROJECT_MANAGEMENT, "BADLY RUN JOBS"],
  [SparkTypeKey.VALUE, "POOR VALUE"],
  [SparkTypeKey.STRANDED, "ABANDONMENT"],
  [SparkTypeKey.PREDATORY_LIEN, "PREDATORY LIEN"],
  [SparkTypeKey.SUBCONTRACTOR_LIEN, "UNPAID SUB LIEN"],
  [SparkTypeKey.UNRELEASED_LIEN, "GHOST LIEN"],
]

const dataByDetectionTypeKey = new Map(
  detectionTypeLabels.map(([typeKey, label], order) => [
    typeKey,
    { label, order },
  ])
)

type DetectionType = { typeKey: string; typeLabel: string }

// A type the list does not name yet keeps its File label, upper-cased.
export const detectionTypeLabel = ({ typeKey, typeLabel }: DetectionType) =>
  dataByDetectionTypeKey.get(typeKey)?.label ?? typeLabel.toUpperCase()

// The short label in sentence case, as a line of prose or a link reads it.
export const detectionSentenceLabel = (detection: DetectionType) =>
  upperFirst(detectionTypeLabel(detection).toLowerCase())

// The type's place in the buyer's reading order; an unlisted type sorts last.
export const detectionTypeOrder = (typeKey: string) =>
  dataByDetectionTypeKey.get(typeKey)?.order ?? Number.MAX_SAFE_INTEGER
